import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import Layout from "../components/layout";
import {
  Row, Col, Card, Calendar, Badge, Pagination, Tag, Flex,
  Radio, Select, theme, Typography, Modal, Descriptions,
  Button, Space, Avatar, Divider, Input, Spin, Empty,
} from "antd";
import Doctor from "../components/Doctor";
import { useDispatch } from "react-redux";
import { showLoading, hideLoading } from "../redux/alertsSlice";
import dayjs from "dayjs";
import "dayjs/locale/zh-cn";
import dayLocaleData from "dayjs/plugin/localeData";
import {
  CalendarOutlined, ClockCircleOutlined,
  HeartOutlined, MedicineBoxOutlined, TeamOutlined,
  CheckCircleOutlined, BellOutlined, SearchOutlined,
  AppstoreOutlined, CloseCircleOutlined,
} from "@ant-design/icons";
dayjs.extend(dayLocaleData);

const { Title, Text, Paragraph } = Typography;

/* ─── Design tokens ─────────────────────────────────────────── */
const EMERALD    = "#0d9488";
const TEAL       = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE      = "#0f172a";
const SLATE_MID  = "#334155";
const MUTED      = "#94a3b8";
const SURFACE    = "#f8fafc";
const WHITE      = "#ffffff";

/* ─── Keyframe injection ─────────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("home-styles")) return;
  const el = document.createElement("style");
  el.id = "home-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

    .home-root { font-family: 'DM Sans', sans-serif; background: ${SURFACE}; min-height: 100vh; }

    .hero-blob {
      position: absolute; top: -120px; right: -100px;
      width: 520px; height: 520px; border-radius: 50%;
      background: radial-gradient(circle, ${TEAL}44 0%, ${EMERALD}22 50%, transparent 70%);
      pointer-events: none; filter: blur(40px);
    }
    .hero-blob-2 {
      position: absolute; bottom: -80px; left: -60px;
      width: 300px; height: 300px; border-radius: 50%;
      background: radial-gradient(circle, ${TEAL}33 0%, transparent 70%);
      pointer-events: none; filter: blur(30px);
    }

    .stat-card { transition: transform 0.25s, box-shadow 0.25s; cursor: default; }
    .stat-card:hover { transform: translateY(-4px); box-shadow: 0 20px 40px rgba(13,148,136,.18) !important; }

    .doctor-col { transition: transform 0.22s; }
    .doctor-col:hover { transform: translateY(-3px); }

    .appt-item { transition: background 0.2s, box-shadow 0.2s; }
    .appt-item:hover { background: #e6f7f5 !important; box-shadow: 0 4px 12px rgba(13,148,136,.12); }

    .appt-badge-pill {
      display: flex; align-items: center; gap: 4px;
      padding: 2px 6px; border-radius: 20px;
      background: ${TEAL_LIGHT}; color: ${EMERALD};
      font-size: 9px; font-weight: 600; margin: 1px 0;
      cursor: pointer; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      transition: background 0.2s;
    }
    .appt-badge-pill:hover { background: #99f6e4; }

    /* ── Service chips ── */
    .service-scroll {
      display: flex; gap: 10px; overflow-x: auto; padding-bottom: 6px;
      scrollbar-width: none;
    }
    .service-scroll::-webkit-scrollbar { display: none; }

    .service-chip {
      display: inline-flex; align-items: center; gap: 8px;
      padding: 10px 20px; border-radius: 50px; cursor: pointer;
      border: 2px solid transparent; white-space: nowrap; flex-shrink: 0;
      font-size: 13px; font-weight: 600; font-family: 'DM Sans', sans-serif;
      transition: all 0.22s cubic-bezier(.4,0,.2,1);
      background: ${WHITE}; color: ${SLATE_MID};
      box-shadow: 0 2px 8px rgba(0,0,0,.06);
    }
    .service-chip:hover {
      border-color: ${TEAL}; color: ${EMERALD};
      box-shadow: 0 4px 16px rgba(13,148,136,.16);
      transform: translateY(-1px);
    }
    .service-chip.active {
      background: linear-gradient(135deg, ${EMERALD}, ${TEAL});
      color: ${WHITE}; border-color: transparent;
      box-shadow: 0 6px 20px rgba(13,148,136,.35);
    }
    .service-chip .chip-icon {
      width: 28px; height: 28px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-size: 14px; background: ${TEAL_LIGHT}; transition: background 0.22s;
    }
    .service-chip.active .chip-icon { background: rgba(255,255,255,.25); }

    /* ── Search bar ── */
    .doctor-search .ant-input {
      background: ${WHITE} !important; border-radius: 14px !important;
      font-family: 'DM Sans', sans-serif; font-size: 14px;
    }
    .doctor-search .ant-input-affix-wrapper {
      border-radius: 14px !important; border: 2px solid #e2e8f0 !important;
      padding: 10px 16px; box-shadow: 0 2px 12px rgba(0,0,0,.06);
      transition: border-color 0.2s, box-shadow 0.2s;
    }
    .doctor-search .ant-input-affix-wrapper:focus-within {
      border-color: ${TEAL} !important;
      box-shadow: 0 0 0 3px rgba(20,184,166,.12) !important;
    }

    @keyframes pulse-ring {
      0% { box-shadow: 0 0 0 0 rgba(20,184,166,.4); }
      70% { box-shadow: 0 0 0 10px rgba(20,184,166,0); }
      100% { box-shadow: 0 0 0 0 rgba(20,184,166,0); }
    }
    .pulse-dot { animation: pulse-ring 2s ease-out infinite; }

    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(24px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .fade-up { animation: fadeUp 0.55s ease both; }
    .fade-up-1 { animation-delay: 0.05s; }
    .fade-up-2 { animation-delay: 0.15s; }
    .fade-up-3 { animation-delay: 0.25s; }
    .fade-up-4 { animation-delay: 0.35s; }

    @keyframes fadeIn {
      from { opacity: 0; transform: scale(0.97); }
      to   { opacity: 1; transform: scale(1); }
    }
    .doctor-grid-item { animation: fadeIn 0.3s ease both; }

    .section-eyebrow {
      display: inline-block; font-family: 'Sora', sans-serif;
      font-size: 11px; font-weight: 700; letter-spacing: 2.5px;
      text-transform: uppercase; color: ${EMERALD};
      padding: 4px 12px; border-radius: 20px;
      background: ${TEAL_LIGHT}; margin-bottom: 12px;
    }

    .tip-card {
      border-left: 4px solid ${TEAL} !important;
      background: linear-gradient(135deg, #f0fdfb 0%, #e6faf7 100%) !important;
    }

    .cal-scroll::-webkit-scrollbar { width: 4px; }
    .cal-scroll::-webkit-scrollbar-thumb { background: ${TEAL_LIGHT}; border-radius: 4px; }

    /* filter tag strip */
    .filter-strip {
      display: flex; align-items: center; gap: 8px;
      padding: 10px 16px; border-radius: 12px;
      background: ${TEAL_LIGHT}; margin-bottom: 20px;
      animation: fadeUp 0.3s ease both;
    }
  `;
  document.head.appendChild(el);
};

/* ─── Helpers ────────────────────────────────────────────────── */
const MORNING_TIPS = [
  "Start your day with a nutritious breakfast to boost energy.",
  "Try a morning stretch to improve flexibility and mood.",
  "Drink a glass of water upon waking to stay hydrated.",
  "Take 10 minutes for mindfulness or meditation to set a positive tone.",
];
const AFTERNOON_TIPS = [
  "Take a short walk after lunch to aid digestion.",
  "Stay mindful of your posture while working to avoid strain.",
  "Snack on fruits or nuts for a healthy energy boost.",
  "Stay hydrated by sipping water throughout the afternoon.",
];
const EVENING_TIPS = [
  "Wind down with a calming tea to promote relaxation.",
  "Limit screen time before bed to improve sleep quality.",
  "Reflect on your day to reduce stress and plan tomorrow.",
  "Read a book to unwind before bedtime.",
];

function getTimeOfDay() {
  const h = dayjs().hour();
  if (h < 12) return "Morning";
  if (h < 18) return "Afternoon";
  return "Evening";
}
function getHealthTip() {
  const t = getTimeOfDay();
  const tips = t === "Morning" ? MORNING_TIPS : t === "Afternoon" ? AFTERNOON_TIPS : EVENING_TIPS;
  return tips[Math.floor(Math.random() * tips.length)];
}

const STATUS_META = {
  approved:    { color: "processing", label: "Confirmed",  tagColor: "cyan" },
  "follow-up": { color: "warning",    label: "Follow-up",  tagColor: "orange" },
  completed:   { color: "success",    label: "Completed",  tagColor: "green" },
  pending:     { color: "default",    label: "Pending",    tagColor: "default" },
  canceled:    { color: "error",      label: "Canceled",   tagColor: "red" },
  rejected:    { color: "error",      label: "Rejected",   tagColor: "red" },
};

/* Service icon mapping – fallback to stethoscope emoji */
const SERVICE_ICONS = {
  cardiology: "🫀", dermatology: "🧴", neurology: "🧠",
  orthopedics: "🦴", pediatrics: "👶", gynecology: "🌸",
  ophthalmology: "👁️", dentistry: "🦷", psychiatry: "🧘",
  general: "🩺", oncology: "🎗️", radiology: "📡",
  urology: "💧", ent: "👂", gastroenterology: "🫁",
};

function serviceIcon(name = "") {
  const key = name.toLowerCase().replace(/\s+/g, "");
  for (const k of Object.keys(SERVICE_ICONS)) {
    if (key.includes(k)) return SERVICE_ICONS[k];
  }
  return "🩺";
}

/* ─── Sub-components ─────────────────────────────────────────── */
function StatCard({ icon, label, value, delay = "" }) {
  return (
    <Card
      className={`stat-card fade-up ${delay}`}
      style={{
        background: `linear-gradient(135deg, ${SLATE} 0%, ${SLATE_MID} 100%)`,
        border: "none", borderRadius: 20, overflow: "hidden", position: "relative",
      }}
      bodyStyle={{ padding: "28px 24px" }}
    >
      <div style={{
        position: "absolute", top: -20, right: -20,
        width: 100, height: 100, borderRadius: "50%",
        background: `${TEAL}22`, pointerEvents: "none"
      }} />
      <Flex align="center" gap={16}>
        <div style={{
          width: 52, height: 52, borderRadius: 16,
          background: `linear-gradient(135deg, ${EMERALD}, ${TEAL})`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 22, color: WHITE, flexShrink: 0,
          boxShadow: `0 8px 20px ${TEAL}55`,
        }}>
          {icon}
        </div>
        <div>
          <Text style={{ color: MUTED, fontSize: 12, fontWeight: 500, display: "block" }}>{label}</Text>
          <Title level={2} style={{ margin: 0, color: WHITE, fontFamily: "'Sora', sans-serif", lineHeight: 1.1 }}>
            {value}
          </Title>
        </div>
      </Flex>
    </Card>
  );
}

function ApptItem({ app, onClick, accentColor = "#0d9488", bg = "#f0fdfb" }) {
  const meta = STATUS_META[app.status] || { label: app.status, tagColor: "default" };
  return (
    <div
      className="appt-item"
      onClick={() => onClick(app)}
      style={{
        padding: "12px 14px", borderRadius: 12,
        borderLeft: `4px solid ${accentColor}`,
        background: bg, cursor: "pointer", marginBottom: 10,
      }}
    >
      <Flex justify="space-between" align="flex-start">
        <div style={{ overflow: "hidden" }}>
          <Text strong style={{ fontSize: 13, display: "block", color: SLATE, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            Dr. {app.doctorInfo?.firstName} {app.doctorInfo?.lastName}
          </Text>
          <Text style={{ fontSize: 11, color: MUTED }}>
            {dayjs(app.schedule_date).format("MMM D, YYYY")} · {app.time}
          </Text>
        </div>
        <Tag color={meta.tagColor} style={{ fontSize: 10, borderRadius: 20, margin: 0, flexShrink: 0 }}>
          {meta.label}
        </Tag>
      </Flex>
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────── */
function Home() {
  const [doctors, setDoctors]             = useState([]);
  const [appointments, setAppointments]   = useState([]);
  const [services, setServices]           = useState([]);
  const [currentPage, setCurrentPage]     = useState(1);
  const [userName, setUserName]           = useState("User");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [healthTip]                       = useState(getHealthTip);

  // ── filter / search state
  const [activeService, setActiveService] = useState(null);   // service name string | null
  const [searchQuery, setSearchQuery]     = useState("");
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);

  const pageSize = 6;
  const dispatch = useDispatch();
  const { token } = theme.useToken();
  const timeOfDay = getTimeOfDay();
  const searchRef = useRef(null);

  useEffect(() => { injectStyles(); }, []);

  /* ── Fetch user ── */
  const fetchUser = useCallback(async () => {
    try {
      dispatch(showLoading());
      const res = await axios.post(
        "/api/user/getUser_id",
        { token: localStorage.getItem("token") },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      dispatch(hideLoading());
      if (res.data.success && res.data.data.name) setUserName(res.data.data.name);
    } catch { dispatch(hideLoading()); }
  }, [dispatch]);

  /* ── Fetch doctors + appointments ── */
  const getData = useCallback(async () => {
    try {
      dispatch(showLoading());
      const [dr, ap] = await Promise.all([
        axios.get("/api/user/get-all-approved-doctors", { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }),
        axios.get("/api/user/get-appointments-by-user-id", { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }),
      ]);
      if (dr.data.success) setDoctors(dr.data.data);
      if (ap.data.success) setAppointments(ap.data.data);
      dispatch(hideLoading());
    } catch { dispatch(hideLoading()); }
  }, [dispatch]);

  /* ── Fetch services ── */
  const fetchServices = useCallback(async () => {
    try {
      setServicesLoading(true);
      const res = await axios.get("/api/user/get-all-services", {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
      if (res.data.success) setServices(res.data.data);
    } catch (e) {
      console.error("Failed to fetch services", e);
    } finally {
      setServicesLoading(false);
    }
  }, []);

  useEffect(() => { fetchUser(); getData(); fetchServices(); }, [fetchUser, getData, fetchServices]);


const fetchFilteredDoctors = useCallback(async (service, query) => {
  try {
    dispatch(showLoading());
    let res;

    if (service) {
      // Call the service-filter endpoint
      res = await axios.get(`/api/user/get-approved-doctors-by-service`, {
        params: { service },
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
    } else if (query && query.trim().length >= 2) {
      // Call the search endpoint
      res = await axios.get(`/api/user/search-doctors`, {
        params: { q: query.trim() },
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
    } else {
      // No filter — use the already-loaded full list
      setFilteredDoctors(doctors);
      setCurrentPage(1);
      dispatch(hideLoading());
      return;
    }

    dispatch(hideLoading());
    if (res.data.success) {
      setFilteredDoctors(res.data.data);
      setCurrentPage(1);
    }
  } catch {
    dispatch(hideLoading());
  }
}, [dispatch, doctors]);







  const displayedDoctors = filteredDoctors;
  const paginatedDoctors = displayedDoctors.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const upcomingAppointments = appointments.filter(app => {
    const dt = dayjs(`${app.schedule_date} ${app.time}`, "YYYY-MM-DD HH:mm");
    return dt.isAfter(dayjs()) && (app.status === "approved" || app.status === "follow-up");
  });
  const completedAppointments = appointments.filter(a => a.status === "completed");

// When a service chip is clicked
const handleServiceClick = (serviceName) => {
  const next = activeService === serviceName ? null : serviceName;
  setActiveService(next);
  setSearchQuery("");
  fetchFilteredDoctors(next, "");
  setTimeout(() => {
    document.getElementById("doctors-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, 80);
};

// When search input changes — debounce to avoid hammering the API
const searchTimeoutRef = useRef(null);

const handleSearchChange = (e) => {
  const q = e.target.value;
  setSearchQuery(q);
  setActiveService(null);
  clearTimeout(searchTimeoutRef.current);
  searchTimeoutRef.current = setTimeout(() => {
    fetchFilteredDoctors(null, q);
  }, 350); // 350ms debounce
};

useEffect(() => {
  setFilteredDoctors(doctors);
}, [doctors]);


const clearFilters = () => {
  setActiveService(null);
  setSearchQuery("");
  setFilteredDoctors(doctors); // reset to the full doctor list
  setCurrentPage(1);
};

  /* ── Calendar helpers ── */
  const getListData = (value) => {
    const date = value.format("YYYY-MM-DD");
    return appointments
      .filter(a => dayjs(a.schedule_date).format("YYYY-MM-DD") === date)
      .map((app, i) => {
        const meta = STATUS_META[app.status] || {};
        return { type: meta.color || "default", content: `Dr. ${app.doctorInfo?.firstName} (${meta.label || app.status})`, appointment: app, index: i };
      });
  };

  const dateCellRender = (value) => {
    const list = getListData(value);
    if (!list.length) return null;
    return (
      <div className="cal-scroll" style={{ maxHeight: 70, overflowY: "auto" }}>
        {list.map(item => (
          <div key={item.index} className="appt-badge-pill"
            onClick={() => { setSelectedAppointment(item.appointment); setIsModalVisible(true); }}
          >
            <Badge status={item.type} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{item.content}</span>
          </div>
        ))}
      </div>
    );
  };

  const monthCellRender = (value) => {
    const start = value.startOf("month");
    const end = value.endOf("month");
    const count = appointments.filter(a => {
      const d = dayjs(a.date);
      return d.isAfter(start) && d.isBefore(end);
    }).length;
    if (!count) return null;
    return <div style={{ textAlign: "center", marginTop: 4 }}><Badge count={count} size="small" style={{ background: EMERALD }} /></div>;
  };

  const calendarHeader = ({ value, type, onChange, onTypeChange }) => {
    const year  = value.year();
    const month = value.month();
    const yearOpts  = Array.from({ length: 10 }, (_, i) => ({ label: `${year - 5 + i}`, value: year - 5 + i }));
    const monthOpts = Array.from({ length: 12 }, (_, i) => ({ label: dayjs().month(i).format("MMMM"), value: i }));
    return (
      <div style={{ padding: "12px 0 16px", borderBottom: `1px solid ${token.colorBorderSecondary}`, marginBottom: 12 }}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <Flex gap={8} align="center" wrap="wrap">
            <Select size="small" value={month} options={monthOpts} style={{ width: 110 }} onChange={m => onChange(value.clone().month(m))} />
            <Select size="small" value={year}  options={yearOpts}  style={{ width: 88 }}  onChange={y => onChange(value.clone().year(y))} />
            <Button size="small" type="primary" ghost style={{ borderColor: EMERALD, color: EMERALD }} onClick={() => onChange(dayjs())}>Today</Button>
          </Flex>
          <Radio.Group size="small" value={type} onChange={e => onTypeChange(e.target.value)} buttonStyle="solid">
            <Radio.Button value="month">Month</Radio.Button>
            <Radio.Button value="year">Year</Radio.Button>
          </Radio.Group>
        </Flex>
      </div>
    );
  };

  const openModal  = (app) => { setSelectedAppointment(app); setIsModalVisible(true); };
  const closeModal = ()    => { setIsModalVisible(false); setSelectedAppointment(null); };

  const isFiltering = !!activeService || !!searchQuery.trim();

  return (
    <Layout>
      <div className="home-root">
        <div style={{ maxWidth: 1240, margin: "0 auto", padding: "24px 20px 60px" }}>

          {/* ── Hero ── */}
          <div
            className="fade-up"
            style={{
              position: "relative", overflow: "hidden",
              background: `linear-gradient(135deg, ${SLATE} 0%, ${SLATE_MID} 100%)`,
              borderRadius: 28, padding: "48px 52px", marginBottom: 36,
              boxShadow: "0 24px 60px rgba(0,0,0,.18)",
            }}
          >
            <div className="hero-blob" />
            <div className="hero-blob-2" />
            <Row gutter={[32, 24]} align="middle">
              <Col xs={24} md={16}>
                <div style={{ position: "relative", zIndex: 1 }}>
                  <div className="section-eyebrow fade-up fade-up-1" style={{ color: TEAL, background: `${TEAL}22` }}>
                    {timeOfDay === "Morning" ? "🌅" : timeOfDay === "Afternoon" ? "☀️" : "🌙"} Good {timeOfDay}
                  </div>
                  <Title
                    level={1}
                    className="fade-up fade-up-2"
                    style={{
                      margin: "8px 0 12px",
                      fontFamily: "'Sora', sans-serif",
                      fontWeight: 800,
                      fontSize: "clamp(2rem, 4vw, 3rem)",
                      lineHeight: 1.15,
                      color: WHITE,
                    }}
                  >
                    Welcome back,<br />
                    <span style={{ backgroundImage: `linear-gradient(90deg, ${TEAL}, #5eead4)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                      {userName}!
                    </span>
                  </Title>
                  <Paragraph className="fade-up fade-up-3" style={{ color: MUTED, fontSize: 15, maxWidth: 480, margin: 0 }}>
                    <HeartOutlined style={{ color: TEAL, marginRight: 6 }} />
                    <em>{healthTip}</em>
                  </Paragraph>
                </div>
              </Col>
              <Col xs={24} md={8}>
                <Card className="tip-card fade-up fade-up-4" style={{ borderRadius: 18, boxShadow: "0 8px 24px rgba(0,0,0,.12)" }} bodyStyle={{ padding: "20px 22px" }}>
                  <Flex align="center" gap={12}>
                    <div className="pulse-dot" style={{
                      width: 44, height: 44, borderRadius: "50%", flexShrink: 0,
                      background: `linear-gradient(135deg, ${EMERALD}, ${TEAL})`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: WHITE, fontSize: 20,
                    }}>
                      <BellOutlined />
                    </div>
                    <div>
                      <Text strong style={{ fontSize: 13, color: SLATE, display: "block" }}>
                        {upcomingAppointments.length > 0
                          ? `${upcomingAppointments.length} upcoming appointment${upcomingAppointments.length > 1 ? "s" : ""}`
                          : "No upcoming appointments"}
                      </Text>
                      {upcomingAppointments.length > 0 && (
                        <Text style={{ fontSize: 11, color: MUTED }}>
                          Next: Dr. {upcomingAppointments[0]?.doctorInfo?.firstName} · {dayjs(upcomingAppointments[0]?.schedule_date).format("MMM D")}
                        </Text>
                      )}
                    </div>
                  </Flex>
                </Card>
              </Col>
            </Row>
          </div>

          {/* ── Stats ── */}
          <Row gutter={[20, 20]} style={{ marginBottom: 44 }}>
            <Col xs={24} sm={8}><StatCard icon={<TeamOutlined />}         label="Available Doctors"      value={doctors.length}               delay="fade-up-1" /></Col>
            <Col xs={24} sm={8}><StatCard icon={<ClockCircleOutlined />}  label="Upcoming Appointments"  value={upcomingAppointments.length}  delay="fade-up-2" /></Col>
            <Col xs={24} sm={8}><StatCard icon={<CheckCircleOutlined />}  label="Completed Visits"       value={completedAppointments.length} delay="fade-up-3" /></Col>
          </Row>

          {/* ══════════════════════════════════════════════════════
              ── Services Section (NEW) ──
          ══════════════════════════════════════════════════════ */}
          <div style={{ marginBottom: 48 }}>
            <Flex align="center" justify="space-between" style={{ marginBottom: 20 }}>
              <div>
                <div className="section-eyebrow">
                  <AppstoreOutlined style={{ marginRight: 5 }} />Our Services
                </div>
                <Title level={3} style={{ margin: 0, fontFamily: "'Sora', sans-serif", color: SLATE }}>
                  Browse by Specialty
                </Title>
              </div>
              {activeService && (
                <Button
                  type="text"
                  icon={<CloseCircleOutlined />}
                  onClick={clearFilters}
                  style={{ color: EMERALD, fontWeight: 600 }}
                >
                  Clear filter
                </Button>
              )}
            </Flex>

            {servicesLoading ? (
              <Flex justify="center" style={{ padding: "24px 0" }}>
                <Spin size="small" />
              </Flex>
            ) : services.length > 0 ? (
              <div className="service-scroll">
                {/* "All" chip */}
                <div
                  className={`service-chip ${!activeService ? "active" : ""}`}
                  onClick={clearFilters}
                >
                  <span className="chip-icon">🏥</span>
                  All Specialists
                </div>

                {services.map((svc, i) => {
                  const name = typeof svc === "string" ? svc : svc.service || svc.name || "";
                  const isActive = activeService === name;
                  return (
                    <div
                      key={svc._id || i}
                      className={`service-chip ${isActive ? "active" : ""}`}
                      onClick={() => handleServiceClick(name)}
                    >
                      <span className="chip-icon">{serviceIcon(name)}</span>
                      {name}
                    </div>
                  );
                })}
              </div>
            ) : (
              <Text style={{ color: MUTED, fontSize: 13 }}>No services available yet.</Text>
            )}
          </div>

          {/* ══════════════════════════════════════════════════════
              ── Doctors Section (with search + filter) ──
          ══════════════════════════════════════════════════════ */}
          <div id="doctors-section" style={{ marginBottom: 52 }}>
            <Flex align="flex-start" justify="space-between" gap={16} wrap="wrap" style={{ marginBottom: 24 }}>
              <div>
                <div className="section-eyebrow">
                  <MedicineBoxOutlined style={{ marginRight: 5 }} />
                  {activeService ? activeService : "Our Specialists"}
                </div>
                <Title level={3} style={{ margin: 0, fontFamily: "'Sora', sans-serif", color: SLATE }}>
                  {activeService
                    ? `${activeService} Doctors`
                    : "Expert Doctors, Ready for You"}
                </Title>
              </div>

              {/* ── Search bar ── */}
              <div style={{ minWidth: 260, flex: "0 0 auto" }}>
               <Input
  ref={searchRef}
  className="doctor-search"
  placeholder="Search by name or specialty…"
  prefix={<SearchOutlined style={{ color: MUTED }} />}
  value={searchQuery}
  onChange={handleSearchChange}   // ← changed
  allowClear
  onClear={() => { clearFilters(); fetchFilteredDoctors(null, ""); }}
  style={{ borderRadius: 14 }}
/>
              </div>
            </Flex>

            {/* Active filter strip */}
            {isFiltering && (
              <div className="filter-strip">
                <SearchOutlined style={{ color: EMERALD }} />
                <Text style={{ color: EMERALD, fontWeight: 600, fontSize: 13 }}>
                  {activeService
                    ? `Showing doctors for: ${activeService}`
                    : `Search results for: "${searchQuery}"`}
                </Text>
                <Text style={{ color: MUTED, fontSize: 12, marginLeft: 6 }}>
                  — {displayedDoctors.length} doctor{displayedDoctors.length !== 1 ? "s" : ""} found
                </Text>
                <Button
                  type="text" size="small" icon={<CloseCircleOutlined />}
                  onClick={clearFilters}
                  style={{ marginLeft: "auto", color: EMERALD }}
                >
                  Clear
                </Button>
              </div>
            )}

            {paginatedDoctors.length > 0 ? (
              <Row gutter={[20, 20]}>
                {paginatedDoctors.map((doctor, i) => (
                  <Col
                    key={doctor._id || i}
                    xs={24} sm={12} md={12} lg={8}
                    className="doctor-col doctor-grid-item"
                    style={{ animationDelay: `${i * 0.06}s` }}
                  >
                    <Doctor doctor={doctor} />
                  </Col>
                ))}
              </Row>
            ) : (
              <div style={{ padding: "48px 0" }}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <span style={{ color: MUTED }}>
                      {isFiltering
                        ? "No doctors match your search. Try a different name or specialty."
                        : "No doctors available."}
                    </span>
                  }
                >
                  {isFiltering && (
                    <Button onClick={clearFilters} style={{ borderColor: EMERALD, color: EMERALD }}>
                      Clear filters
                    </Button>
                  )}
                </Empty>
              </div>
            )}

            {displayedDoctors.length > pageSize && (
              <Flex justify="center" style={{ marginTop: 32 }}>
                <Pagination
                  current={currentPage}
                  total={displayedDoctors.length}
                  pageSize={pageSize}
                  onChange={setCurrentPage}
                  showSizeChanger={false}
                />
              </Flex>
            )}
          </div>

          {/* ── Calendar + Sidebar ── */}
          <div>
            <div className="section-eyebrow" style={{ marginBottom: 20 }}>
              <CalendarOutlined style={{ marginRight: 5 }} />Schedule
            </div>
            <Row gutter={[24, 24]}>
              {/* Calendar */}
              <Col xs={24} lg={16}>
                <Card bordered={false} style={{ borderRadius: 20, boxShadow: "0 8px 32px rgba(0,0,0,.06)" }} bodyStyle={{ padding: 20 }}>
                  <Title level={4} style={{ margin: "0 0 4px", fontFamily: "'Sora', sans-serif", color: SLATE }}>
                    Appointment Calendar
                  </Title>
                  <Text style={{ color: MUTED, fontSize: 12 }}>Click any appointment to see details</Text>
                  <Divider style={{ margin: "16px 0" }} />
                  <div style={{ height: 460, overflow: "hidden" }}>
                    <Calendar
                      fullscreen={false}
                      headerRender={calendarHeader}
                      cellRender={dateCellRender}
                      monthCellRender={monthCellRender}
                    />
                  </div>
                </Card>
              </Col>

              {/* Sidebar */}
              <Col xs={24} lg={8}>
                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                  <Card bordered={false} style={{ borderRadius: 20, boxShadow: "0 8px 32px rgba(0,0,0,.06)" }} bodyStyle={{ padding: "20px 20px 12px" }}>
                    <Flex align="center" gap={8} style={{ marginBottom: 14 }}>
                      <div style={{ width: 32, height: 32, borderRadius: 10, background: TEAL_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", color: EMERALD }}>
                        <ClockCircleOutlined />
                      </div>
                      <Text strong style={{ color: SLATE, fontSize: 14 }}>Upcoming</Text>
                      {upcomingAppointments.length > 0 && (
                        <Badge count={upcomingAppointments.length} style={{ background: EMERALD, marginLeft: "auto" }} />
                      )}
                    </Flex>
                    {upcomingAppointments.length > 0 ? (
                      upcomingAppointments.slice(0, 5).map(app => (
                        <ApptItem key={app._id} app={app} onClick={openModal} accentColor={EMERALD} bg="#f0fdfb" />
                      ))
                    ) : (
                      <div style={{ textAlign: "center", padding: "24px 0", color: MUTED }}>
                        <CalendarOutlined style={{ fontSize: 28, marginBottom: 8, display: "block" }} />
                        <Text style={{ color: MUTED, fontSize: 12 }}>No upcoming appointments</Text>
                      </div>
                    )}
                  </Card>

                  <Card bordered={false} style={{ borderRadius: 20, boxShadow: "0 8px 32px rgba(0,0,0,.06)" }} bodyStyle={{ padding: "20px 20px 12px" }}>
                    <Flex align="center" gap={8} style={{ marginBottom: 14 }}>
                      <div style={{ width: 32, height: 32, borderRadius: 10, background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", color: "#16a34a" }}>
                        <CheckCircleOutlined />
                      </div>
                      <Text strong style={{ color: SLATE, fontSize: 14 }}>Completed</Text>
                    </Flex>
                    {completedAppointments.length > 0 ? (
                      completedAppointments.slice(0, 5).map(app => (
                        <ApptItem key={app._id} app={app} onClick={openModal} accentColor="#22c55e" bg="#f0fdf4" />
                      ))
                    ) : (
                      <div style={{ textAlign: "center", padding: "24px 0" }}>
                        <Text style={{ color: MUTED, fontSize: 12 }}>No completed appointments</Text>
                      </div>
                    )}
                  </Card>
                </Space>
              </Col>
            </Row>
          </div>

        </div>
      </div>

      {/* ── Appointment Detail Modal ── */}
      <Modal
        title={
          <Flex align="center" gap={10}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: TEAL_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", color: EMERALD }}>
              <CalendarOutlined />
            </div>
            <Text strong style={{ fontSize: 15, color: SLATE }}>Appointment Details</Text>
          </Flex>
        }
        open={isModalVisible}
        onOk={closeModal}
        onCancel={closeModal}
        width={560}
        footer={<Button onClick={closeModal} style={{ borderRadius: 8 }}>Close</Button>}
        styles={{ body: { paddingTop: 16 } }}
      >
        {selectedAppointment && (() => {
          const doc  = selectedAppointment.doctorInfo;
          const meta = STATUS_META[selectedAppointment.status] || { label: selectedAppointment.status, tagColor: "default" };
          return (
            <div>
              <div style={{ padding: "16px 20px", borderRadius: 14, background: SURFACE, marginBottom: 16, border: `1px solid ${token.colorBorderSecondary}` }}>
                <Flex align="center" gap={14}>
                  <Avatar size={52} style={{ background: `linear-gradient(135deg, ${EMERALD}, ${TEAL})`, fontSize: 20 }}>
                    {doc?.firstName?.[0]}
                  </Avatar>
                  <div>
                    <Text strong style={{ fontSize: 15, color: SLATE, display: "block" }}>
                      Dr. {doc?.firstName} {doc?.lastName}
                    </Text>
                    <Text style={{ color: MUTED, fontSize: 12 }}>{doc?.specialization || "General Practitioner"}</Text>
                  </div>
                  <Tag color={meta.tagColor} style={{ marginLeft: "auto", borderRadius: 20, fontWeight: 600 }}>
                    {meta.label}
                  </Tag>
                </Flex>
              </div>
              <Descriptions column={1} size="small" bordered labelStyle={{ color: MUTED, fontWeight: 500, width: 130 }}>
                <Descriptions.Item label={<><CalendarOutlined style={{ marginRight: 6 }} />Date</>}>
                  {dayjs(selectedAppointment.schedule_date).format("dddd, MMMM D, YYYY")}
                </Descriptions.Item>
                <Descriptions.Item label={<><ClockCircleOutlined style={{ marginRight: 6 }} />Time</>}>
                  {selectedAppointment.time}
                </Descriptions.Item>
                <Descriptions.Item label="Reason">
                  {selectedAppointment.reason || "General consultation"}
                </Descriptions.Item>
                <Descriptions.Item label="Notes">
                  {selectedAppointment.notes || "No additional notes provided"}
                </Descriptions.Item>
              </Descriptions>
            </div>
          );
        })()}
      </Modal>
    </Layout>
  );
}

export default Home;